import React, { useCallback, useMemo } from 'react';
import { Archive, ArrowRight, FileText, GitBranch, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { HtmlRenderer } from '../shared/HtmlRenderer';
import { PlanUsedSkillsTags } from './PlanUsedSkillsTags';

const TIME_FRAME_LABEL_KEYS = {
    yesterday: 'prioritiesPanel.timeFrames.yesterday',
    week_to_date: 'prioritiesPanel.timeFrames.weekToDate',
    last_week: 'prioritiesPanel.timeFrames.lastWeek',
    month_to_date: 'prioritiesPanel.timeFrames.monthToDate',
    last_month: 'prioritiesPanel.timeFrames.lastMonth',
    custom: 'prioritiesPanel.timeFrames.customRange',
};

function getPriorityBadge(priority, t) {
    switch (priority?.toUpperCase()) {
        case 'HIGH':
            return { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200', label: t('prioritiesPanel.priorityHigh') };
        case 'MEDIUM':
            return { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-200', label: t('prioritiesPanel.priorityMedium') };
        default:
            return { bg: 'bg-sky-50', text: 'text-sky-800', border: 'border-sky-200', label: t('prioritiesPanel.priorityLow') };
    }
}

function formatStepPreview(step, t) {
    if (!step) return '';
    const trimmed = String(step).trim();
    if (/^step\s*\d+/i.test(trimmed)) return trimmed;
    return t('prioritiesPanel.stepPreviewPrefix', { text: trimmed });
}

/**
 * Filter card (date range, priority, report) + Products list for the priorities page.
 */
export function RecommendedPrioritiesFullPanel({
    databaseId,
    navigate,
    allDatabasePlans,
    reportsList,
    priorityFilter,
    setPriorityFilter,
    topicFilter,
    setTopicFilter,
    timeFrameFilter,
    setTimeFrameFilter,
    includeArchivedPlans,
    setIncludeArchivedPlans,
    handleSetPlanStatus,
    loading = false,
}) {
    const { t } = useTranslation('report-panels');

    const getReportName = useCallback(
        (reportGenerationId) => {
            const report = reportsList.find(
                (r) => r.id === reportGenerationId || r.report_generation_id === reportGenerationId,
            );
            return report?.topic || report?.title || t('prioritiesPanel.reportFallback', { id: reportGenerationId });
        },
        [reportsList, t],
    );

    const filteredPlans = useMemo(() => {
        return allDatabasePlans.filter((plan) => {
            if (!includeArchivedPlans) {
                if (plan.status === 'archived' || plan.status === 'superseded') return false;
            }
            if (priorityFilter && plan.priority?.toUpperCase() !== priorityFilter) return false;
            if (topicFilter && String(plan.report_generation_id ?? '') !== String(topicFilter)) return false;
            if (timeFrameFilter && plan.time_frame_type !== timeFrameFilter) return false;
            return true;
        });
    }, [allDatabasePlans, includeArchivedPlans, priorityFilter, topicFilter, timeFrameFilter]);

    const sortedPlans = useMemo(() => {
        return [...filteredPlans].sort((a, b) => {
            const na = getReportName(a.report_generation_id);
            const nb = getReportName(b.report_generation_id);
            if (na !== nb) return na.localeCompare(nb);
            const pa = ['HIGH', 'MEDIUM', 'LOW'].indexOf(String(a.priority || '').toUpperCase());
            const pb = ['HIGH', 'MEDIUM', 'LOW'].indexOf(String(b.priority || '').toUpperCase());
            if (pa !== pb) return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
            return (b.id || 0) - (a.id || 0);
        });
    }, [filteredPlans, getReportName]);

    const segmentBtn = (active) =>
        `min-w-[72px] flex-1 px-2 sm:px-3 py-2 text-xs font-medium transition-all whitespace-nowrap border-r border-gray-200 last:border-r-0 ${
            active ? 'bg-primary-50 text-primary-800' : 'bg-white text-gray-600 hover:bg-gray-50'
        }`;

    return (
        <div className="w-full space-y-6">
            <Card padding="md">
                <div className="flex items-center justify-between mb-6">
                    <span className="text-base font-semibold text-gray-900">{t('prioritiesPanel.filter')}</span>
                    <button
                        type="button"
                        onClick={() => setIncludeArchivedPlans(!includeArchivedPlans)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                            includeArchivedPlans
                                ? 'bg-gray-50 text-gray-800 border-gray-300'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        title={includeArchivedPlans ? t('prioritiesPanel.showingAllIncludingArchived') : t('prioritiesPanel.showingActiveOnly')}
                    >
                        {includeArchivedPlans ? t('prioritiesPanel.all') : t('prioritiesPanel.active')}
                    </button>
                </div>

                <div className="flex flex-col xl:flex-row gap-8 xl:gap-10 xl:items-end">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 mb-2">{t('prioritiesPanel.dateRange')}</p>
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white">
                            {[
                                { value: 'yesterday', label: t('prioritiesPanel.timeFrames.yesterday') },
                                { value: 'week_to_date', label: t('prioritiesPanel.timeFrames.weekToDate') },
                                { value: 'last_week', label: t('prioritiesPanel.timeFrames.lastWeek') },
                                { value: 'month_to_date', label: t('prioritiesPanel.timeFrames.monthToDate') },
                                { value: 'last_month', label: t('prioritiesPanel.timeFrames.lastMonth') },
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setTimeFrameFilter(timeFrameFilter === value ? '' : value)}
                                    className={segmentBtn(timeFrameFilter === value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex-shrink-0">
                        <p className="text-xs font-medium text-gray-500 mb-2">{t('prioritiesPanel.priority')}</p>
                        <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden bg-white">
                            {[
                                { value: 'HIGH', label: t('prioritiesPanel.priorityOptions.high') },
                                { value: 'MEDIUM', label: t('prioritiesPanel.priorityOptions.medium') },
                                { value: 'LOW', label: t('prioritiesPanel.priorityOptions.low') },
                            ].map(({ value, label }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setPriorityFilter(priorityFilter === value ? '' : value)}
                                    className={`px-4 py-2 text-xs font-medium transition-all border-r border-gray-200 last:border-r-0 ${
                                        priorityFilter === value
                                            ? 'bg-primary-50 text-primary-800'
                                            : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex-shrink-0 w-full sm:w-56 xl:w-48">
                        <p className="text-xs font-medium text-gray-500 mb-2">{t('prioritiesPanel.report')}</p>
                        <select
                            value={topicFilter}
                            onChange={(e) => setTopicFilter(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer appearance-none bg-[length:1rem] bg-[right_0.5rem_center] bg-no-repeat pr-9"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                            }}
                        >
                            <option value="">{t('prioritiesPanel.allReport')}</option>
                            {reportsList.map((report) => (
                                <option key={report.id} value={report.id}>
                                    {report.topic}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </Card>

            <Card
                padding="md"
                className="min-h-[280px]"
                title={(
                    <span className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary-600" />
                        Products
                    </span>
                )}
                headerClassName="border-gray-100 px-6 py-4"
            >
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500 text-sm">
                            <Loader className="w-8 h-8 animate-spin text-primary-600 mb-3" />
                            Loading recommendations…
                        </div>
                    ) : allDatabasePlans.length === 0 ? (
                        <div className="text-center py-14 px-4">
                            <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations yet</h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">
                                Generate reports to see AI-powered recommendations and priorities.
                            </p>
                        </div>
                    ) : sortedPlans.length === 0 ? (
                        <div className="text-center py-14 px-4">
                            <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('prioritiesPanel.noMatchingTitle')}</h3>
                            <p className="text-sm text-gray-500">{t('prioritiesPanel.noMatchingBody')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {sortedPlans.map((plan) => {
                                const priorityBadge = getPriorityBadge(plan.priority, t);
                                const tfLabel =
                                    (TIME_FRAME_LABEL_KEYS[plan.time_frame_type] && t(TIME_FRAME_LABEL_KEYS[plan.time_frame_type])) ||
                                    plan.time_frame_type?.replace(/_/g, ' ') ||
                                    '';
                                const ownerLine = plan.owner || t('prioritiesPanel.unassigned');
                                const stepText = formatStepPreview(plan.steps?.[0], t);

                                return (
                                    <Card
                                        key={plan.id}
                                        padding="base"
                                        className="bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all text-left"
                                    >
                                        {plan.highlight_chart && (
                                            <div className="mb-4 rounded-lg border border-gray-200 bg-white overflow-hidden">
                                                <HtmlRenderer
                                                    htmlContent={plan.highlight_chart}
                                                    className="w-full block"
                                                    minHeight={280}
                                                />
                                            </div>
                                        )}
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <h3 className="text-base font-semibold text-gray-900 leading-snug pr-2">
                                                {plan.topic}
                                            </h3>
                                            {plan.priority && (
                                                <span
                                                    className={`px-2.5 py-0.5 text-xs font-bold rounded border flex-shrink-0 uppercase tracking-wide ${priorityBadge.bg} ${priorityBadge.text} ${priorityBadge.border}`}
                                                >
                                                    {priorityBadge.label}
                                                </span>
                                            )}
                                        </div>
                                        {stepText && (
                                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
                                                {stepText}
                                            </p>
                                        )}
                                        <PlanUsedSkillsTags skills={plan.skills} className="mb-3" />
                                        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-200">
                                            <div className="text-xs text-gray-500 min-w-0">
                                                <span>{tfLabel}</span>
                                                <span className="text-gray-300 mx-1.5">·</span>
                                                <span className="truncate">{ownerLine}</span>
                                                {plan.status === 'superseded' && (
                                                    <>
                                                        <span className="text-gray-300 mx-1.5">·</span>
                                                        <span className="inline-flex items-center gap-0.5 text-amber-800 font-medium">
                                                            <GitBranch className="w-3 h-3 inline" />
                                                            Snapshot
                                                        </span>
                                                    </>
                                                )}
                                                {plan.status === 'archived' && (
                                                    <>
                                                        <span className="text-gray-300 mx-1.5">·</span>
                                                        <span>Archived</span>
                                                    </>
                                                )}
                                                {plan.supersedes_plan_id && plan.status !== 'superseded' && (
                                                    <>
                                                        <span className="text-gray-300 mx-1.5">·</span>
                                                        <span className="text-sky-700">From #{plan.supersedes_plan_id}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                {plan.continued_by_plan_id ? (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            navigate(
                                                                `/reports/${databaseId}/plan?planId=${plan.continued_by_plan_id}&reportId=${plan.report_generation_id}&timeFrameType=${plan.time_frame_type}`,
                                                            )
                                                        }
                                                        className="text-xs font-semibold text-primary-600 hover:text-primary-800 mr-2 hidden sm:inline"
                                                    >
                                                        Current →
                                                    </button>
                                                ) : null}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSetPlanStatus(
                                                            plan.id,
                                                            plan.status === 'archived' ? 'active' : 'archived',
                                                        );
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                                                    title={plan.status === 'archived' ? 'Unarchive' : 'Archive'}
                                                >
                                                    <Archive className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(
                                                            `/reports/${databaseId}/plan?planId=${plan.id}&reportId=${plan.report_generation_id}&timeFrameType=${plan.time_frame_type}`,
                                                        )
                                                    }
                                                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-md transition-colors"
                                                    aria-label="Open plan"
                                                >
                                                    <ArrowRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
            </Card>
        </div>
    );
}
